import { container } from "tsyringe";
import { constructor } from "tsyringe/dist/typings/types";

type DependencyScope = "viewModel" | "otherRepo";
type ModuleState = "initializing" | "ready";

export interface InjectionBinding<T = any> {
  token: string | symbol;
  useClass: constructor<T>;
  scope: DependencyScope;
}

export interface IModuleInjection {
  initialize(): void;
  readonly viewModelToken?: string | symbol;
}

export class DependencyInjections {
  static register<T>(
    token: string | symbol,
    implClass: constructor<T>,
    scope: DependencyScope,
  ): void {
    if (container.isRegistered(token)) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`[DI] Duplicate token registration: ${String(token)}`, {
          attemptedUseClass: implClass.name,
          attemptedScope: scope,
        });
      }

      return;
    }

    if (scope === "otherRepo") {
      container.registerSingleton<T>(token, implClass);
      return;
    }

    container.register<T>(token, {
      useClass: implClass,
    });
  }

  static resolve<T>(token: string | symbol): T {
    if (!token) {
      throw new Error("Dependency token is undefined");
    }

    if (!container.isRegistered(token)) {
      throw new Error(`Unregistered token: ${String(token)}`);
    }

    return container.resolve<T>(token);
  }

  static getRepo<T>(token: string | symbol): T {
    return this.resolve<T>(token);
  }

  static lateResolution<T>(token: string | symbol): T {
    return this.resolve<T>(token);
  }

  static isRegistered(token: string | symbol): boolean {
    return container.isRegistered(token);
  }
}

export abstract class InjectionModule {
  protected static bindings: InjectionBinding[] = [];
  private static registrationStates = new WeakMap<Function, ModuleState>();

  protected static resolve<T>(token: string | symbol): T {
    return DependencyInjections.resolve<T>(token);
  }

  static initialize(): void {
    const state = InjectionModule.registrationStates.get(this);
    const warmupTokens: Array<string | symbol> = [];

    if (state === "ready") return;
    if (state === "initializing") {
      throw new Error(`Circular InjectionModule: ${this.name}`);
    }
    InjectionModule.registrationStates.set(this, "initializing");

    try {
      this.bindings.forEach(({ token, useClass, scope }) => {
        DependencyInjections.register(token, useClass, scope);
        if (scope === "otherRepo") {
          warmupTokens?.push(token);
        }
      });
      warmupTokens?.forEach((token) => this.resolve(token));
      InjectionModule.registrationStates.set(this, "ready");
    } catch (error) {
      InjectionModule.registrationStates.delete(this);
      throw error;
    }
  }

  static get viewModelToken(): string | symbol | undefined {
    const viewModelBindings = this.bindings.filter(
      ({ scope }) => scope === "viewModel",
    );

    return viewModelBindings.length === 1
      ? viewModelBindings[0].token
      : undefined;
  }
}
